import React from "react";
import {
  createContext,
  useState,
  ReactNode,
  FC,
  useEffect,
  useCallback,
} from "react";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import { UserData } from "../types/types";
import { useAuth } from "../hooks/useAuthContext";

interface UserContextState {
  user: UserData | null;
  /** Await this after writing users/<uid>: the effect below fires on the auth
      change, which for a brand-new account races the document being created. */
  fetchUser: (userId?: string) => Promise<void>;
}

export const UserContext = createContext<UserContextState | undefined>(
  undefined,
);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const { user: authUser } = useAuth();

  const fetchUser = useCallback(async (userId?: string): Promise<void> => {
    if (!userId) {
      setUser(null);
      return;
    }
    const userDocRef = doc(firestore, "users", userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      setUser(docSnap.data() as UserData);
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    fetchUser(authUser?.uid);
  }, [authUser?.uid, fetchUser]);

  return (
    <UserContext.Provider value={{ user, fetchUser }}>
      {children}
    </UserContext.Provider>
  );
};
